/**
 * Users.tsx — F10 T10.8 (tenant scope) — strona zarzadzania kontami uzytkownikow.
 *
 * Zgodne z `docs/plan.md §1522-1526`:
 *   - Tabela: display_name, email, user_type, is_super_user, **lista companies z rolami**,
 *     status, ostatnie logowanie.
 *   - Filtry: tylko human / api / service; zawieszeni; z kontem vs bez.
 *   - Modal: dodaj konto integracyjne (dla human → osobne wdrozenie Pracownikow F10).
 *   - Sekcja "Dostępy do company" inline edit dropdown roli per firma (auto-save).
 *   - "Uprawnienia per kategoria kontrahentów" → placeholder F11 (kategorie kontrahentow
 *     dojda w F11).
 *
 * Drawer matryca inline edit jest wersja zwiezla (tylko dropdown roli per firma);
 * pelen edytor z dosypanymi uprawnieniami zostaje w `Settings → Uprawnienia uzytkownikow`
 * (T10.10 UsersPermissionsTab) — link prowadzi do tej zakladki.
 *
 * Endpointy:
 *   GET    /api/users                              lista (limit=100 MVP)
 *   GET    /api/users?include_deleted=true         lista z koszem (perm `users.view_deleted`)
 *   GET    /api/users/:userId                      drawer szczegolow
 *   POST   /api/users                              utworz konto api/service
 *   POST   /api/users/invite                       zaproszenie human (initial)
 *   POST   /api/users/:userId/resend-invite        wyslij ponownie zaproszenie (status=invited)
 *   POST   /api/users/:userId/suspend              zawies (body { reason })
 *   POST   /api/users/:userId/activate             aktywuj
 *   DELETE /api/users/:userId                      soft-delete
 *   POST   /api/users/:userId/restore              przywroc z kosza
 *   POST   /api/companies/:companyId/members       upsert role assignment (auto-save)
 *
 * Backend nie zwraca tokena API przy POST /users (passwordHash=NULL, brak issuance
 * tokenow do F11). Modal pokazuje info "konto utworzone, token w F11+".
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { isValidUsername } from '@saas/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  AlertTriangle,
  KeyRound,
  LogOut,
  Mail,
  MoreHorizontal,
  Plus,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Users as UsersIcon,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListTopWidgets,
  ListViewModeToggle,
  QuickFilterBar,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import { CustomRowMenuItems } from '@/components/configurable-table/CustomActionButtonBar';
import type { RowActionItem } from '@/components/configurable-table/RowActionsMenu';
import { useServerListControls } from '@/components/configurable-table/useServerListControls';
import { Drawer } from '@/components/Drawer';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { GroupChipBar } from '@/components/groups/GroupChipBar';
import { InfoHint } from '@/components/InfoHint';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { AddCompanyForm, CompanyAccessRow } from '@/components/settings/UsersPermissionsTab';
import { Spinner } from '@/components/Spinner';
import { TenantUserChangeIdentifierModal } from '@/components/TenantUserChangeIdentifierModal';
import { TenantUserSetTempPasswordModal } from '@/components/TenantUserSetTempPasswordModal';
import { showUserRestoredToasts } from '@/components/users/restore-toasts';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useFormDraft } from '@/hooks/useFormDraft';
import { useReportRecordLock } from '@/hooks/useReportRecordLock';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import { extractFieldErrors } from '@/lib/errors';
import {
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type CoreColumnOverride,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListActions } from '@/lib/list-actions/useListActions';
import { useEngineListExport } from '@/lib/list-export/useEngineListExport';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';
import {
  buildUsersListFilterParams,
  type CompanyListItem,
  useCompanies,
  USER_PERMISSIONS_KEY,
  type UserCompanyAccess,
  type UserListItem,
  useRoleTemplates,
  USERS_LIST_KEY,
  useUserPermissions,
  useUsersList,
} from '@/lib/use-permissions';
import {
  USER_DETAIL_KEY,
  type UserDetail,
  type UserRestoreResponse,
  useUserDetail,
} from '@/lib/use-tenant-people';

type UserType = 'human' | 'api' | 'service';
type StatusType = 'active' | 'invited' | 'suspended';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// Awatar-placeholder z inicjałem — miękkie pastelowe tło + nasycony inicjał, kolor wyliczony
// DETERMINISTYCZNIE z nazwy (ta sama osoba = zawsze ten sam kolor), parytet z makietą Figma.
const AVATAR_PALETTE = [
  'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
];
function avatarPalette(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length] ?? AVATAR_PALETTE[0] ?? '';
}

// ============================================================================
// Strona
// ============================================================================

export function Users() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { me } = useTenantAuth();

  // Przełącznik „kosza" (gcrecord) zdjęty z kartotek (PHASE 25): „Usuń" = miękkie usunięcie
  // bez widoku odzyskiwania w UI. Archiwum (flaga) jest wyłącznie na dokumentach.
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  /**
   * Filtr chipów nad listą = GRUPY ze słownika `user_groups` (domknięcie projektu grup dla
   * użytkowników, 2026-08-18 — kontrahenci i pracownicy mają to od 0116). Wiele grup naraz,
   * semantyka OR.
   */
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  // Filtry per kolumna z popupu w nagłówku + wiersz „Szukaj…" pod nagłówkami.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  // Sort serwerowy + paginacja (silnik list) — mirror `PurchaseInvoices.tsx`/`Employees.tsx`.
  // `defaultPageSize: 25` = dawny domyślny limit `useUsersList` (poprzednio bez pagera, teraz
  // stronicowane jak reszta list na silniku). `sortableSlugs`/`filterableSlugs` z `meta` ostatniej
  // odpowiedzi (REALNY stan — kolumny muszą się przeliczyć, gdy metadane dotrą).
  const controls = useServerListControls({ defaultPageSize: 25 });
  // Kolumny FAKTYCZNIE widoczne w tabeli (`onVisibleColumnsChange`) — źródło kolumn pliku CSV
  // (E16). `null` = tabela jeszcze nie zgłosiła widoczności.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Zmiana filtra/sortu wraca na pierwszą stronę. Zawinięte tutaj, bo `headerFilters`/
  // `columnFilters` mają DWA callsite'y (`ListFilterBar` + `ConfigurableTable`).
  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );
  const handleColumnFiltersChange = useCallback(
    (next: ColumnFilters) => {
      setColumnFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );

  const forcedListLayoutId = useForcedListLayoutId('users');
  const tableId = forcedListLayoutId ? `users::layout-${forcedListLayoutId}` : 'users';

  // Dokleja `?mi=<id>` do nawigacji w głąb (nowy/detal), żeby formularz użytkownika
  // znał kontekst pozycji menu — URL detalu jest osobną nawigacją, nie dziedziczy
  // query stringa listy.
  const withMenuItem = useWithMenuItem();

  // Szybkie filtry (presety z list_config) — chipy nad listą.
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId);
  const quickFilters = useMemo(
    () => listLayout?.layoutConfig?.quickFilters ?? [],
    [listLayout?.layoutConfig?.quickFilters],
  );
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);
  // Tryb widoku sterowany na poziomie strony — przełącznik w nagłówku (spójnie z
  // Kontrahentami i modułami Kreatora). ConfigurableTable dostaje go jako prop i
  // wygasza własny wbudowany pasek przełącznika. Pastylki = tryby dostępne w bieżącym
  // layoucie (raport tabeli, m.in. kalendarz tylko z konfiguracją) — wspólny hook.
  const viewModes = useListViewMode();
  useEffect(() => {
    if (activeQuickFilterId === null && quickFilters.length > 0) {
      const def = quickFilters.find((q) => q.isDefault);
      if (def) setActiveQuickFilterId(def.id);
    }
  }, [quickFilters, activeQuickFilterId]);

  const companiesQ = useCompanies();
  const companies = useMemo(() => companiesQ.data?.companies ?? [], [companiesQ.data?.companies]);

  // Katalog pól modułu `users` (core + ext) — źródło WSZYSTKICH kolumn (rdzennych i dodatkowych)
  // generowanych dynamicznie poniżej (`kartoteka-listify` użytkownicy).
  const fieldsQ = useSystemModuleFields('users');
  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`), aktualizowany niżej PO `usersQ`.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();

  const extColumns = useMemo<ColumnDefinition<UserListItem>[]>(
    () => buildExtColumns<UserListItem>(fieldsQ.data ?? [], t, getRefLabels),
    [fieldsQ.data, t, getRefLabels],
  );

  // Rejestr nadpisań kolumn rdzennych — render bogaty tam, gdzie generyk (typ→komórka) nie
  // odtwarza wiernie stary render: „Użytkownik" (awatar + nazwa/email + link + odznaki
  // super-user/skasowany), „Typ konta"/„Status" (plakietki), „Super-user" (odznaka, kolumna
  // WYDZIELONA z dawnej kompozytowej komórki „Użytkownik"), „Ostatnie logowanie" (relative time).
  // `email`/`username`/`language`/`invited_at` NIE mają nadpisania — generyk (`buildCoreColumns`)
  // już produkuje dokładnie żądany kontrakt (tekst + filtr tekstowy sterowany `meta.filterable`,
  // nie flagą katalogu `isFilterable`, bo `resolveFilterable` jest wpięty niżej).
  // Budowany WEWNĄTRZ komponentu (`useMemo`) — domyka `withMenuItem`/`i18n.language`, których
  // rejestr modułowy (poza komponentem) by nie widział (mirror `Employees.tsx` EMPLOYEE_OVERRIDES).
  const USER_OVERRIDES = useMemo<Record<string, CoreColumnOverride<UserListItem>>>(
    () => ({
      display_name: ({ field, resolveSortable }) => ({
        id: field.slug,
        accessor: (row) => row.displayName ?? row.email,
        header: field.name,
        defaultVisible: true,
        defaultWidth: 280,
        // Kolumna „na chama" niehideowalna — kotwica identyfikacji wiersza (link do detalu,
        // odznaki super-user/skasowany); companion `avatar_path` (niżej) zawsze dołączony.
        hideable: false,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: { category: 'text', fieldSlug: 'display_name' },
        renderCell: (_value, row) => {
          const isDeleted = row.gcrecord !== null;
          const seed = row.displayName ?? row.email;
          const initial = seed.charAt(0).toUpperCase();
          return (
            <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
              {/* Serwowanie pełnego URL awatara (presigned) to follow-up poza zakresem tej
                  migracji — `avatarPath` dziś niesie surowy klucz MinIO, nie gotowy `<img src>`
                  (patrz `MyProfile.tsx`), więc zostaje placeholder z inicjałem w kolorze
                  wyliczonym deterministycznie z nazwy (parytet z Figmą). */}
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${avatarPalette(seed)}`}
              >
                {initial}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Link
                    to={withMenuItem(`/users/${row.id}`)}
                    className={`font-medium hover:underline ${
                      isDeleted ? 'text-text-tertiary' : 'text-accent-700 dark:text-accent-300'
                    }`}
                  >
                    {row.displayName ?? row.email}
                  </Link>
                  {isDeleted && (
                    <Badge color="gray" data-testid={`deleted-badge-${row.id}`}>
                      {t('tenant.users.badge.deleted')}
                    </Badge>
                  )}
                </div>
                {row.displayName && <div className="text-xs text-text-muted">{row.email}</div>}
              </div>
            </div>
          );
        },
      }),
      // Język jako chip z flagą + kodem (parytet z projektem Figma) — zamiast gołego „pl".
      language: ({ field, accessor, resolveSortable }) => ({
        id: field.slug,
        accessor,
        header: field.name,
        defaultVisible: true,
        defaultWidth: 90,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: {
          category: 'select',
          fieldSlug: 'language',
          transformLabel: (value) =>
            value ? String(value).toUpperCase() : t('common.lists.emptyValue'),
        },
        renderCell: (value) => <LanguageBadge language={value == null ? null : String(value)} />,
      }),
      user_type: ({ field, accessor, resolveSortable }) => ({
        id: field.slug,
        accessor,
        header: field.name,
        defaultVisible: true,
        defaultWidth: 130,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: {
          category: 'select',
          fieldSlug: 'user_type',
          transformLabel: (value) =>
            value
              ? t(`tenant.users.userType.${value}`, { defaultValue: String(value) })
              : t('common.lists.emptyValue'),
        },
        renderCell: (_value, row) => <UserTypeBadge userType={row.userType} />,
      }),
      // Odznaka super-usera — WYDZIELONA z dawnej kompozytowej komórki „Użytkownik" do własnej
      // kolumny katalogu (task #listify). `data-testid` zachowany 1:1 (`super-badge-<id>`).
      is_super_user: ({ field, resolveSortable }) => ({
        id: field.slug,
        accessor: 'isSuperUser',
        header: field.name,
        defaultVisible: true,
        defaultWidth: 140,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: { category: 'select', fieldSlug: 'is_super_user' },
        renderCell: (_value, row) =>
          row.isSuperUser ? (
            <Badge
              color="red"
              className="border border-red-300 bg-red-50 text-red-600 dark:border-red-800"
              data-testid={`super-badge-${row.id}`}
            >
              {t('tenant.users.badge.superUser')}
            </Badge>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      }),
      status: ({ field, accessor, resolveSortable }) => ({
        id: field.slug,
        accessor,
        header: field.name,
        defaultVisible: true,
        defaultWidth: 130,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: {
          category: 'select',
          fieldSlug: 'status',
          transformLabel: (value) =>
            value
              ? t(`tenant.users.status.${value}`, { defaultValue: String(value) })
              : t('common.lists.emptyValue'),
        },
        renderCell: (_value, row) => <UserStatusBadge status={row.status} />,
      }),
      last_login_at: ({ field, accessor, resolveSortable }) => ({
        id: field.slug,
        accessor,
        header: field.name,
        defaultVisible: true,
        defaultWidth: 160,
        sortable: resolveSortable?.(field) ?? false,
        // Brak `headerFilter` (jak w dawnej kolumnie) — `filterable: false` jawnie, inaczej
        // ConfigurableTable pokazałby martwy input „Szukaj…" bez `fieldSlug` (mirror wzorca
        // z `org_unit_id`/`manager_employee_id` w `Employees.tsx`).
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-muted" data-testid={`last-login-${row.id}`}>
            {formatLastLogin(row.lastLoginAt, i18n.language, t)}
          </span>
        ),
      }),
    }),
    [t, i18n.language, withMenuItem],
  );

  // Kolumny rdzenne z katalogu (klucz mechaniczny ze sluga; garstka przez rejestr nadpisań).
  // Bez `excludeSlugs` — `default_company_id`/`groups` mają `showInList=false` w katalogu (chip-bar
  // grup nad tabelą i przyszły dedykowany widżet, nie kolumny listy).
  const coreColumns = useMemo<ColumnDefinition<UserListItem>[]>(
    () =>
      buildCoreColumns<UserListItem>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels,
        overrides: USER_OVERRIDES,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
      }),
    [
      fieldsQ.data,
      t,
      getRefLabels,
      USER_OVERRIDES,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
    ],
  );

  // Kolumna „Firmy" — relacja (pigułki firm z rolami), poza katalogiem pól (płaska projekcja
  // silnika jej nie złoży — trasa dokleja `companies[]` osobnym zapytaniem, patrz
  // `user-list-descriptor.ts`). Bez `headerFilter` (relacja, nie pole filtrowalne silnika);
  // `filterable: false` jawnie — inaczej martwy input „Szukaj…" bez `fieldSlug`.
  const columns = useMemo<ColumnDefinition<UserListItem>[]>(
    () => [
      ...coreColumns,
      {
        id: 'companies',
        accessor: (row) => row.companies.map((c) => c.shortName ?? c.legalName).join(', '),
        header: t('tenant.users.col.companies'),
        defaultVisible: true,
        defaultWidth: 240,
        sortable: false,
        filterable: false,
        renderCell: (_value, row) =>
          row.companies.length === 0 ? (
            <span className="text-xs text-text-muted">{t('tenant.users.noCompanies')}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {row.companies.map((c) => {
                const label = c.shortName ?? c.legalName;
                // Wszystkie role osoby w firmie (ADR-0103); fallback na rolę główną dla starych danych.
                const roleNames =
                  c.roles && c.roles.length > 0
                    ? c.roles.map((r) => r.name).join(', ')
                    : c.roleTemplateName;
                const role = roleNames ? ` · ${roleNames}` : t('tenant.users.noRoleSuffix');
                return (
                  <span
                    key={c.id}
                    className="inline-flex rounded-lg bg-bg-tertiary px-2 py-0.5 text-xs text-text-secondary"
                    title={role.replace(/^ · /, '')}
                  >
                    {label}
                    <span className="text-text-muted">{role}</span>
                  </span>
                );
              })}
            </div>
          ),
      },
      // Kolumna „Kategoria" — nazwa z `record_categories` (LEFT JOIN w deskryptorze, accessor
      // `categoryName`). Kolumna RĘCZNA (jak „Firmy"), NIE z katalogu pól: filtr to warunek
      // tekstowy po nazwie (`conditionOnly` — pomija /distinct, bo to kolumna liczona z joina),
      // sort serwerowy po slugu `category` (parytet z Employees.tsx).
      {
        id: 'category',
        accessor: 'categoryName',
        header: t('tenant.recordCategories.picker.label'),
        defaultVisible: true,
        defaultWidth: 180,
        sortable: controls.isCoreColumnSortable('category', sortableSlugs),
        renderCell: (value) =>
          value == null || value === '' ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            String(value)
          ),
        headerFilter: { category: 'text', fieldSlug: 'category', conditionOnly: true },
      },
      ...extColumns,
    ],
    [coreColumns, t, extColumns, controls.isCoreColumnSortable, sortableSlugs],
  );

  // Mapa id kolumny → slug backendu dla `?columns=`. id = slug dla wszystkie kolumny rdzenne
  // (buildCoreColumns); „Firmy"/„Kategoria" są kolumnami WIRTUALNYMI (poza katalogiem) — mapowane jawnie.
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = { companies: 'companies', category: 'category' };
    for (const c of coreColumns) map[c.id] = c.id; // id = slug (buildCoreColumns)
    return map;
  }, [coreColumns]);

  // Slugi aktualnie widocznych kolumn (wspólny helper) → `?columns=`, żeby zapytanie listy
  // ciągnęło tylko renderowane dane. „Zawsze dołącz" (niezależne od widoczności kolumn) —
  // pola czytane POZA własną komórką widocznej kolumny: `gcrecord` (akcje wiersza + odznaka
  // skasowany), `email` (fallback tekstu w modalach zawieś/usuń/wyloguj/reset — budowane z
  // `row` menu akcji, nie z accessora kolumny), `user_type`/`status` (gating menu akcji: reset
  // hasła i wyloguj tylko dla `human`, resend tylko dla `invited`), `is_super_user`/`companies`
  // (baner „brak dostępu do firmy", liczony z załadowanej strony). Towarzysz: awatar w komórce
  // „Użytkownik" (display_name).
  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns: listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet: new Set(extColumns.map((c) => c.id)),
        alwaysSlugs: ['gcrecord', 'email', 'user_type', 'status', 'is_super_user', 'companies'],
        companions: [{ whenSlug: 'display_name', addSlug: 'avatar_path' }],
        // Pola kalendarza układu zawsze w `?columns=` — kalendarz nie zależy od widocznych kolumn.
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extColumns,
    ],
  );

  // Filtry do backendu jako pojedyncza tablica `headerFilters`:
  // (1) popup w nagłówku + (2) input „Szukaj…" pod headerem + (3) aktywny preset.
  const viewHeaderFilters = useMemo(() => {
    const merged: ColumnHeaderFilter[] = [];
    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }
    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }
    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    if (activeQf) {
      for (const cond of activeQf.filters) {
        const col = columns.find((c) => c.id === cond.field);
        const fieldSlug = col?.headerFilter?.fieldSlug;
        if (!fieldSlug) continue;
        merged.push({
          field: fieldSlug,
          mode: 'condition',
          operator: cond.operator as ColumnHeaderFilter['operator'],
          conditionValue: cond.value == null ? '' : String(cond.value),
        });
      }
    }
    return merged;
  }, [headerFilters, debouncedColumnFilters, quickFilters, activeQuickFilterId, columns]);
  // Widok kalendarza dociąga tylko rekordy z wyświetlanego miesiąca (wspólny filtr z silnika) —
  // tylko zapytanie listy. Eksport CSV (E16) bierze same filtry widoku: miesiąc to nawigacja.
  const exportHeaderFilters =
    viewHeaderFilters.length > 0 ? JSON.stringify(viewHeaderFilters) : undefined;
  const serializedHeaderFilters = controls.calendarHeaderFilter
    ? JSON.stringify([...viewHeaderFilters, controls.calendarHeaderFilter])
    : exportHeaderFilters;

  // Aktywna firma (CompanySwitcher) — listing scope'owany po stronie backendu
  // przez `?companyId=` (brak => unia Model B, patrz `use-permissions.ts`).
  const companyScope = useActiveCompanyScope();
  const companyId = companyIdParam(companyScope);

  // Zmiana zakresu firmy (selektor globalny) też zawęża wynik — reset strony analogicznie.
  useEffect(() => {
    controls.resetPage();
  }, [companyId, controls.resetPage]);

  // Silnik list (sort serwerowy + paginacja) — `?columns=` z `visibleColumnParams` (projekcja),
  // `sort`/`order`/`limit`/`offset` z `controls.queryParams`.
  // Filtry listy BEZ stronicowania i projekcji — wspólne dla zapytania listy i eksportu CSV (E16:
  // plik zawiera dokładnie to, co lista). Parametry składa jedna funkcja `buildUsersListFilterParams`.
  const listFilterOptions = {
    groups: selectedGroups.length > 0 ? selectedGroups : undefined,
    search: debouncedSearch || undefined,
    headerFilters: exportHeaderFilters,
    companyId,
    sort: controls.queryParams.sort,
    order: controls.queryParams.order,
  };
  const buildListFilterParams = () => buildUsersListFilterParams(listFilterOptions);
  const usersQ = useUsersList({
    ...listFilterOptions,
    headerFilters: serializedHeaderFilters,
    limit: controls.queryParams.limit,
    offset: controls.queryParams.offset,
    columns: visibleColumnParams,
  });
  setRefLabels(usersQ.data?.extRefLabels);
  // `meta.sortable`/`meta.filterable` w stan przez EFEKT, nie w renderze (to useState → wywołanie
  // w renderze pętli).
  useEffect(() => {
    setSortableSlugs(usersQ.data?.meta?.sortable);
  }, [usersQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(usersQ.data?.meta?.filterable);
  }, [usersQ.data?.meta?.filterable]);
  const users = useMemo(() => usersQ.data?.users ?? [], [usersQ.data?.users]);

  // Baner-nudge: konta ludzi (nie super-user, nie z kosza) bez dostępu do ŻADNEJ firmy.
  // Bez dostępu do firmy user nie widzi danych firmowych (Model B) — admin powinien go
  // dopisać. Liczymy z aktualnie załadowanej listy (to, co admin ma przed oczami).
  const noAccessCount = useMemo(
    () =>
      users.filter(
        (u) =>
          u.userType === 'human' &&
          !u.isSuperUser &&
          u.gcrecord === null &&
          u.companies.length === 0,
      ).length,
    [users],
  );

  // Menu akcji wiersza (portal-positioned).
  const [openMenuFor, setOpenMenuFor] = useState<{ id: string; top: number; right: number } | null>(
    null,
  );

  // Drawer
  const initialUserId = searchParams.get('userId');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(initialUserId);
  const userDetailQ = useUserDetail(selectedUserId);

  // Modale
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [suspendingUser, setSuspendingUser] = useState<UserListItem | null>(null);
  const [deletingUser, setDeletingUser] = useState<UserListItem | null>(null);
  const [logoutAllUser, setLogoutAllUser] = useState<UserListItem | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<UserListItem | null>(null);
  // ADR-0022: change-identifier + set-temp-password (gated permission).
  const [changeIdentifierUser, setChangeIdentifierUser] = useState<UserListItem | null>(null);
  const [setTempPasswordUser, setSetTempPasswordUser] = useState<UserListItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Eksport CSV (E16) — dokładnie to, co lista. „Eksport listy": te same filtry co zapytanie
  // listy (wyszukiwarka, grupy, filtry kolumn, szybki filtr, sort, wybrana firma), wszystkie
  // strony. „Eksport zaznaczonych": tylko wskazane ID (+ firma i sort listy). Plik zawiera
  // WYŁĄCZNIE kolumny widoczne w tabeli, w jej kolejności i z jej nagłówkami.
  const listExport = useEngineListExport<UserListItem>({
    target: 'users',
    filename: 'uzytkownicy',
    buildFilterParams: buildListFilterParams,
    columns,
    visibleColumnIds,
    // Dopóki katalog pól i układ listy się ładują, eksport nie startuje (niepełna lista kolumn).
    ready: fieldsQ.isSuccess && !layoutLoading,
    columnSlug: (id) => coreIdToSlug[id] ?? (extColumns.some((c) => c.id === id) ? id : undefined),
    // Etykiety odwołań z odpowiedzi eksportu (wszystkie wiersze, nie tylko bieżąca strona) —
    // te same buildery kolumn rdzennych i dodatkowych co tabela; „Firmy"/„Kategoria" bez odwołań.
    buildExportColumns: (refLabels) => {
      const getLabels = () => refLabels as ExtRefLabels | undefined;
      return [
        ...buildCoreColumns<UserListItem>({
          fields: fieldsQ.data ?? [],
          t,
          getRefLabels: getLabels,
          overrides: USER_OVERRIDES,
        }),
        ...columns.filter((c) => c.id === 'companies' || c.id === 'category'),
        ...buildExtColumns<UserListItem>(fieldsQ.data ?? [], t, getLabels),
      ];
    },
    // Komórki z ozdobnikami (awatar, plakietki, flaga) → sama wartość, jak etykieta na ekranie.
    textOverrides: {
      display_name: (row) => row.displayName ?? row.email,
      language: (row) =>
        String((row as unknown as Record<string, unknown>).language ?? '').toUpperCase(),
      user_type: (row) => t(`tenant.users.userType.${row.userType}`),
      status: (row) => t(`tenant.users.status.${row.status}`),
      is_super_user: (row) => (row.isSuperUser ? t('tenant.users.badge.superUser') : ''),
      companies: userCompaniesCsvText,
    },
    layoutColumns: listLayout?.layoutConfig?.columns,
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: t('tenant.users.title'),
  });

  // Akcje listy (PHASE 25) — generyczny hook. Eksporty (zaznaczonych i całej listy) składa strona
  // z widocznych kolumn (`handlers`); import bez modalu → „wkrótce". Archiwizacja/przywracanie =
  // rozgłoszenie po ID. Lokalne menu wiersza (blokada/reset hasła/…) nietknięte.
  const userActions = useListActions({
    kartotekaKey: 'users',
    entity: 'users',
    queryKey: USERS_LIST_KEY,
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    handlers: { ...listExport.handlers },
    testIdPrefix: 'users',
  });

  // Sync drawer state ↔ URL (?userId=...)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedUserId) next.set('userId', selectedUserId);
    else next.delete('userId');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  // Usunięcie i przywrócenie konta to zwykły zapis rekordu — na czas zadania przycisku SQL na nim serwer
  // odpowiada 409 `RECORD_LOCKED_BY_RUN`; zamiast ogólnego błędu pokazujemy, które zadanie blokuje konto.
  const reportRecordLock = useReportRecordLock();

  // ----- Mutacje -----
  const suspendMutation = useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason: string }) => {
      const res = await api.post(`/users/${userId}/suspend`, { reason });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(vars.userId) });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'users'] });
      toast.show({ variant: 'success', title: t('tenant.users.toast.suspended') });
      setSuspendingUser(null);
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.users.toast.suspendError'),
  });

  const activateMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.post(`/users/${userId}/activate`, {});
      return res.data;
    },
    onSuccess: (_d, userId) => {
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(userId) });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'users'] });
      toast.show({ variant: 'success', title: t('tenant.users.toast.activated') });
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.users.toast.activateError'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.delete(`/users/${userId}`);
      return res.data;
    },
    onSuccess: (_d, userId) => {
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'users'] });
      if (selectedUserId === userId) setSelectedUserId(null);
      toast.show({ variant: 'success', title: t('tenant.users.toast.deleted') });
      setDeletingUser(null);
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      handleApiError(err, t, toast, 'tenant.users.toast.deleteError');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.post<UserRestoreResponse>(`/users/${userId}/restore`, {});
      return res.data;
    },
    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(userId) });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'users'] });
      showUserRestoredToasts(toast, t, data);
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      handleApiError(err, t, toast, 'tenant.users.toast.restoreError');
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.post(`/users/${userId}/resend-invite`, {});
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({ variant: 'success', title: t('tenant.users.toast.resendInviteSent') });
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.users.toast.resendInviteError'),
  });

  const logoutAllMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.post<{ revokedCount: number; userId: string }>(
        `/users/${userId}/logout-all`,
        {},
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.show({
        variant: 'success',
        title: t('tenant.users.toast.logoutAllDone'),
        description: t('tenant.users.toast.logoutAllCount', { count: data.revokedCount }),
      });
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.users.toast.logoutAllError'),
  });

  const triggerResetMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.post(`/users/${userId}/trigger-password-reset`, {});
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.users.toast.passwordResetSent'),
        description: t('tenant.users.toast.passwordResetSentDescription'),
      });
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.users.toast.passwordResetError'),
  });

  // ----- Render -----
  return (
    <main className="flex h-full min-h-0 flex-col bg-bg-secondary p-6" data-testid="users-page">
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Nagłówek FLAT wg Figmy (123:24680): tytuł + opis po lewej; po prawej przełącznik
            widoku, Operacje (Akcje), Kreator i „Dodaj". Bez karty-nagłówka. */}
        <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-text-primary">{t('tenant.users.title')}</h1>
            <p className="mt-1 text-sm text-text-tertiary">{t('tenant.users.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="users" />
            <ListActionsMenu {...userActions.menuProps} />
            <ListBuilderMenu slug="users" testIdPrefix="users" />
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate(withMenuItem('/users/new'))}
              leftIcon={<Plus size={16} aria-hidden="true" />}
              data-testid="users-add-btn"
            >
              {t('tenant.users.addCta')}
            </Button>
          </div>
        </header>

        {noAccessCount > 0 && (
          <div
            className="flex shrink-0 items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
            role="status"
            data-testid="users-no-access-banner"
          >
            <AlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium">{t('tenant.users.noAccessBanner.title')}</p>
              <p className="text-xs text-amber-700 dark:text-amber-400/90">
                {t('tenant.users.noAccessBanner.description', { count: noAccessCount })}
              </p>
            </div>
          </div>
        )}

        <div className="shrink-0">
          <ListTopWidgets tableId={tableId} categoryId={null} companyId={companyId} />
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {fieldsQ.isError ? (
            <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
          ) : (
            <ConfigurableTable<UserListItem>
              tableId={tableId}
              testIdPrefix="users"
              fillHeight
              showOrdinal
              /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): chipy grup po lewej,
                 baton odśwież/filtry po prawej; presety w osobnym rzędzie pod spodem. */
              listToolbar={
                <div className="flex flex-col gap-2">
                  {/* Jedna linia: grupy po lewej; po prawej Akcje, Kreator, przełącznik widoku, odśwież. */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Chipy filtra = GRUPY ze słownika (wielokrotny wybór, OR). */}
                    <GroupChipBar
                      dictionaryCode="user_groups"
                      selected={selectedGroups}
                      onChange={(next) => {
                        setSelectedGroups(next);
                        controls.resetPage();
                      }}
                      className="flex-1"
                      data-testid="users-group-chip-bar"
                    />
                    <div className="ml-auto flex shrink-0 items-center gap-3">
                      {/* Licznik kont (wg Figmy „5 kont") — obok przycisku odświeżania. */}
                      <span className="text-[13px] text-text-tertiary" data-testid="users-count">
                        {t('tenant.users.accountCount', { count: usersQ.data?.total ?? 0 })}
                      </span>
                      <ListFilterBar
                        tableId={tableId}
                        columns={columns}
                        search={search}
                        onSearchChange={(next) => {
                          setSearch(next);
                          controls.resetPage();
                        }}
                        searchPlaceholder={t('tenant.users.filters.searchPlaceholder')}
                        showSearch={false}
                        headerFilters={headerFilters}
                        onHeaderFiltersChange={handleHeaderFiltersChange}
                        onRefresh={() =>
                          queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY })
                        }
                        testIdPrefix="users"
                      />
                    </div>
                  </div>
                  <QuickFilterBar
                    quickFilters={quickFilters}
                    activeId={activeQuickFilterId}
                    onChange={(id) => {
                      setActiveQuickFilterId(id);
                      controls.resetPage();
                    }}
                  />
                </div>
              }
              {...viewModes.tableProps}
              columns={columns}
              data={users}
              isLoading={usersQ.isLoading}
              getRowId={(row) => row.id}
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onRowContextMenu={userActions.onRowContextMenu}
              onVisibleColumnsChange={setVisibleColumnIds}
              headerFilters={headerFilters}
              onHeaderFiltersChange={handleHeaderFiltersChange}
              filterable
              columnFilters={columnFilters}
              onColumnFiltersChange={handleColumnFiltersChange}
              {...controls.tableProps}
              totalItems={usersQ.data?.total ?? 0}
              isFetching={usersQ.isFetching}
              onCalendarRangeChange={controls.setCalendarRange}
              onRowClick={(row) => navigate(withMenuItem(`/users/${row.id}`))}
              renderActions={(row) => (
                <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="rounded p-1.5 text-text-muted hover:bg-bg-secondary"
                    onClick={(e) => {
                      if (openMenuFor?.id === row.id) {
                        setOpenMenuFor(null);
                        return;
                      }
                      const rect = e.currentTarget.getBoundingClientRect();
                      const dropdownH = 280;
                      const spaceBelow = window.innerHeight - rect.bottom;
                      const top = spaceBelow < dropdownH ? rect.top - dropdownH : rect.bottom + 4;
                      setOpenMenuFor({ id: row.id, top, right: window.innerWidth - rect.right });
                    }}
                    aria-label={t('tenant.users.actions.menuAria')}
                    data-testid={`users-actions-${row.id}`}
                  >
                    <MoreHorizontal size={16} aria-hidden="true" />
                  </button>
                  {openMenuFor?.id === row.id && (
                    <UserRowActionsMenu
                      user={row}
                      customItems={userActions.rowButtonItems(row.id)}
                      menuPos={{ top: openMenuFor.top, right: openMenuFor.right }}
                      onClose={() => setOpenMenuFor(null)}
                      onOpenDetails={() => {
                        navigate(withMenuItem(`/users/${row.id}`));
                        setOpenMenuFor(null);
                      }}
                      onSuspend={() => {
                        setSuspendingUser(row);
                        setOpenMenuFor(null);
                      }}
                      onActivate={() => {
                        activateMutation.mutate(row.id);
                        setOpenMenuFor(null);
                      }}
                      onDelete={() => {
                        setDeletingUser(row);
                        setOpenMenuFor(null);
                      }}
                      onRestore={() => {
                        restoreMutation.mutate(row.id);
                        setOpenMenuFor(null);
                      }}
                      onResendInvite={() => {
                        resendInviteMutation.mutate(row.id);
                        setOpenMenuFor(null);
                      }}
                      onLogoutAll={() => {
                        setLogoutAllUser(row);
                        setOpenMenuFor(null);
                      }}
                      onTriggerReset={() => {
                        setResetPasswordUser(row);
                        setOpenMenuFor(null);
                      }}
                    />
                  )}
                </div>
              )}
              actionsColumnLabel={t('tenant.users.col.actions')}
              emptyIcon={UsersIcon}
              emptyTitle={t('tenant.users.empty')}
            />
          )}
        </div>
      </div>

      {/* Drawer szczegolow */}
      <UserDetailDrawer
        open={selectedUserId !== null}
        onClose={() => setSelectedUserId(null)}
        detail={userDetailQ.data?.user ?? null}
        loading={userDetailQ.isLoading}
        onSuspend={(u) => setSuspendingUser(userDetailToListItem(u))}
        onActivate={(u) => activateMutation.mutate(u.id)}
        onDelete={(u) => setDeletingUser(userDetailToListItem(u))}
        onRestore={(u) => restoreMutation.mutate(u.id)}
        onResendInvite={(u) => resendInviteMutation.mutate(u.id)}
        onLogoutAll={(u) => setLogoutAllUser(userDetailToListItem(u))}
        onTriggerReset={(u) => setResetPasswordUser(userDetailToListItem(u))}
        onChangeIdentifier={(u) => setChangeIdentifierUser(userDetailToListItem(u))}
        onSetTempPassword={(u) => setSetTempPasswordUser(userDetailToListItem(u))}
      />

      {/* Modal "Dodaj usera" */}
      <CreateUserModal
        open={createModalOpen}
        companies={companies}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
          setCreateModalOpen(false);
        }}
      />

      {/* Modal "Zawies" */}
      <SuspendUserModal
        user={suspendingUser}
        onClose={() => setSuspendingUser(null)}
        onConfirm={(reason) => {
          if (suspendingUser) suspendMutation.mutate({ userId: suspendingUser.id, reason });
        }}
        submitting={suspendMutation.isPending}
      />

      {/* Modal "Usun" */}
      <DeleteUserModal
        user={deletingUser}
        onClose={() => setDeletingUser(null)}
        onConfirm={() => {
          if (deletingUser) deleteMutation.mutate(deletingUser.id);
        }}
        submitting={deleteMutation.isPending}
      />

      {/* Modal "Wyloguj ze wszystkich sesji" */}
      <LogoutAllUserModal
        user={logoutAllUser}
        onClose={() => setLogoutAllUser(null)}
        onConfirm={() => {
          if (logoutAllUser) {
            logoutAllMutation.mutate(logoutAllUser.id, {
              onSuccess: () => setLogoutAllUser(null),
            });
          }
        }}
        submitting={logoutAllMutation.isPending}
      />

      {/* Modal "Resetuj haslo (mail)" */}
      <TriggerPasswordResetModal
        user={resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
        onConfirm={() => {
          if (resetPasswordUser) {
            triggerResetMutation.mutate(resetPasswordUser.id, {
              onSuccess: () => setResetPasswordUser(null),
            });
          }
        }}
        submitting={triggerResetMutation.isPending}
      />

      {/* ADR-0022: Modal "Zmiana danych logowania" */}
      <TenantUserChangeIdentifierModal
        open={changeIdentifierUser !== null}
        user={
          changeIdentifierUser
            ? {
                id: changeIdentifierUser.id,
                email: changeIdentifierUser.email,
                username:
                  (changeIdentifierUser as UserListItem & { username?: string | null }).username ??
                  null,
                displayName: changeIdentifierUser.displayName,
              }
            : null
        }
        onClose={() => setChangeIdentifierUser(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
          if (changeIdentifierUser) {
            queryClient.invalidateQueries({
              queryKey: USER_DETAIL_KEY(changeIdentifierUser.id),
            });
          }
        }}
      />

      {/* ADR-0022: Modal "Ustaw tymczasowe hasło" */}
      <TenantUserSetTempPasswordModal
        open={setTempPasswordUser !== null}
        user={
          setTempPasswordUser
            ? {
                id: setTempPasswordUser.id,
                email: setTempPasswordUser.email,
                displayName: setTempPasswordUser.displayName,
              }
            : null
        }
        onClose={() => setSetTempPasswordUser(null)}
      />

      {userActions.overlays}
    </main>
  );
}

// ============================================================================
// UserRowActionsMenu — portal menu akcji per wiersz (fixed-position + backdrop)
// ============================================================================

function UserRowActionsMenu({
  user,
  customItems,
  menuPos,
  onClose,
  onOpenDetails,
  onSuspend,
  onActivate,
  onDelete,
  onRestore,
  onResendInvite,
  onLogoutAll,
  onTriggerReset,
}: {
  user: UserListItem;
  /** Własne przyciski „Akcja wiersza" z Kreatora Listy. */
  customItems: RowActionItem[];
  menuPos: { top: number; right: number };
  onClose: () => void;
  onOpenDetails: () => void;
  onSuspend: () => void;
  onActivate: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onResendInvite: () => void;
  onLogoutAll: () => void;
  onTriggerReset: () => void;
}) {
  const { t } = useTranslation();
  const isDeleted = user.gcrecord !== null;
  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden="true"
        data-testid="row-menu-backdrop"
      />
      <div
        style={{ top: menuPos.top, right: menuPos.right }}
        className="fixed z-50 w-56 rounded-lg border border-border-primary bg-bg-primary py-1 shadow-lg"
        role="menu"
        data-testid={`users-row-menu-popup-${user.id}`}
      >
        {!isDeleted && <CustomRowMenuItems items={customItems} onAfterSelect={onClose} />}
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary"
          onClick={onOpenDetails}
        >
          {t('tenant.users.actions.openDetails')}
        </button>
        {!isDeleted && (
          <>
            {user.status === 'suspended' ? (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary"
                onClick={onActivate}
                data-testid={`users-action-activate-${user.id}`}
              >
                <ShieldCheck size={14} aria-hidden="true" />
                {t('tenant.users.actions.activate')}
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary"
                onClick={onSuspend}
                data-testid={`users-action-suspend-${user.id}`}
              >
                <ShieldAlert size={14} aria-hidden="true" />
                {t('tenant.users.actions.suspend')}
              </button>
            )}
            {user.status === 'invited' && (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary"
                onClick={onResendInvite}
                data-testid={`users-action-resend-${user.id}`}
              >
                <Mail size={14} aria-hidden="true" />
                {t('tenant.users.actions.resendInvite')}
              </button>
            )}
            {user.userType === 'human' && user.status !== 'invited' && (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary"
                onClick={onTriggerReset}
                data-testid={`users-action-reset-password-${user.id}`}
              >
                <KeyRound size={14} aria-hidden="true" />
                {t('tenant.users.actions.triggerPasswordReset')}
              </button>
            )}
            {user.userType === 'human' && user.status !== 'invited' && (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary"
                onClick={onLogoutAll}
                data-testid={`users-action-logout-all-${user.id}`}
              >
                <LogOut size={14} aria-hidden="true" />
                {t('tenant.users.actions.logoutAll')}
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-bg-tertiary"
              onClick={onDelete}
              data-testid={`users-action-delete-${user.id}`}
            >
              <Trash2 size={14} aria-hidden="true" />
              {t('tenant.users.actions.delete')}
            </button>
          </>
        )}
        {isDeleted && (
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary"
            onClick={onRestore}
            data-testid={`users-action-restore-${user.id}`}
          >
            <RotateCcw size={14} aria-hidden="true" />
            {t('tenant.users.actions.restore')}
          </button>
        )}
      </div>
    </>
  );
}

// Flaga kraju jako SVG (emoji-flagi nie renderują się na Windowsie — pokazują litery).
// Obsługiwane pl/en; inne języki → brak grafiki, sam kod.
function FlagIcon({ code }: { code: string }) {
  const common = {
    width: 18,
    height: 12,
    viewBox: '0 0 20 14',
    className: 'inline-block flex-shrink-0 rounded-[2px] ring-1 ring-black/10 align-middle',
    'aria-hidden': true as const,
  };
  if (code === 'pl') {
    return (
      <svg {...common}>
        <rect width="20" height="7" fill="#ffffff" />
        <rect y="7" width="20" height="7" fill="#dc143c" />
      </svg>
    );
  }
  if (code === 'en') {
    return (
      <svg {...common}>
        <rect width="20" height="14" fill="#012169" />
        <path d="M0 0 L20 14 M20 0 L0 14" stroke="#ffffff" strokeWidth="2.8" />
        <path d="M0 0 L20 14 M20 0 L0 14" stroke="#c8102e" strokeWidth="1.2" />
        <path d="M10 0 V14 M0 7 H20" stroke="#ffffff" strokeWidth="4" />
        <path d="M10 0 V14 M0 7 H20" stroke="#c8102e" strokeWidth="2" />
      </svg>
    );
  }
  return null;
}

/**
 * Kolumna „Firmy" w pliku CSV (E16): `Firma (Rola1, Rola2); Firma B` — nazwa jak na pigułce
 * (skrócona, inaczej pełna), role w nawiasie (bez ról → sama nazwa), firmy po średniku.
 */
function userCompaniesCsvText(row: UserListItem): string {
  return row.companies
    .map((c) => {
      const label = c.shortName ?? c.legalName;
      const roleNames =
        c.roles && c.roles.length > 0
          ? c.roles.map((r) => r.name)
          : c.roleTemplateName
            ? [c.roleTemplateName]
            : [];
      return roleNames.length > 0 ? `${label} (${roleNames.join(', ')})` : label;
    })
    .join('; ');
}

function LanguageBadge({ language }: { language?: string | null }) {
  if (!language) return <span className="text-text-tertiary">—</span>;
  const code = String(language).toLowerCase();
  return (
    <Badge color="gray" data-testid={`lang-badge-${code}`}>
      <span className="mr-1 inline-flex items-center">
        <FlagIcon code={code} />
      </span>
      {code.toUpperCase()}
    </Badge>
  );
}

function UserTypeBadge({ userType }: { userType: UserListItem['userType'] }) {
  const { t } = useTranslation();
  const map: Record<UserListItem['userType'], { color: 'gray' | 'blue' | 'amber'; key: string }> = {
    human: { color: 'gray', key: 'tenant.users.userType.human' },
    api: { color: 'blue', key: 'tenant.users.userType.api' },
    service: { color: 'blue', key: 'tenant.users.userType.service' },
    impersonation: { color: 'amber', key: 'tenant.users.userType.impersonation' },
  };
  const cfg = map[userType];
  return <Badge color={cfg.color}>{t(cfg.key)}</Badge>;
}

function UserStatusBadge({ status }: { status: UserListItem['status'] }) {
  const { t } = useTranslation();
  const map: Record<UserListItem['status'], { color: 'green' | 'amber' | 'red'; key: string }> = {
    active: { color: 'green', key: 'tenant.users.status.active' },
    invited: { color: 'amber', key: 'tenant.users.status.invited' },
    suspended: { color: 'red', key: 'tenant.users.status.suspended' },
  };
  const cfg = map[status];
  return (
    <Badge color={cfg.color} data-testid={`status-badge-${status}`}>
      <span
        className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-current"
        aria-hidden="true"
      />
      {t(cfg.key)}
    </Badge>
  );
}

// ============================================================================
// Drawer szczegolow + mini-matryca inline edit roli per firma
// ============================================================================

interface UserDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  detail: UserDetail | null;
  loading: boolean;
  onSuspend: (u: UserDetail) => void;
  onActivate: (u: UserDetail) => void;
  onDelete: (u: UserDetail) => void;
  onRestore: (u: UserDetail) => void;
  onResendInvite: (u: UserDetail) => void;
  onLogoutAll: (u: UserDetail) => void;
  onTriggerReset: (u: UserDetail) => void;
  onChangeIdentifier: (u: UserDetail) => void;
  onSetTempPassword: (u: UserDetail) => void;
}

function UserDetailDrawer({
  open,
  onClose,
  detail,
  loading,
  onSuspend,
  onActivate,
  onDelete,
  onRestore,
  onResendInvite,
  onLogoutAll,
  onTriggerReset,
  onChangeIdentifier,
  onSetTempPassword,
}: UserDetailDrawerProps) {
  const { t } = useTranslation();
  const isDeleted = detail?.gcrecord !== null && detail?.gcrecord !== undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={detail ? (detail.displayName ?? detail.email) : t('tenant.users.drawer.loadingTitle')}
      description={detail?.email}
    >
      {loading || !detail ? (
        <div className="flex items-center justify-center py-10" role="status">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-5" data-testid="user-drawer">
          {/* Sekcja: Dane podstawowe */}
          <DrawerBasicsSection detail={detail} />

          {/* Sekcja: Dostepy do firm — mini-matryca inline edit */}
          {detail.userType === 'human' && <DrawerCompaniesSection detail={detail} />}

          {/* Sekcja: Uprawnienia per kategoria kontrahentow — placeholder F11 */}
          {detail.userType === 'human' && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                {t('tenant.users.drawer.contractorCategoryAccess')}
              </h3>
              <div className="rounded-lg border border-dashed border-border-secondary px-3 py-3 text-xs text-text-tertiary">
                {t('tenant.users.drawer.contractorCategoryAccessF11')}
              </div>
            </section>
          )}

          {/* Sekcja: Sesje (logout-all) — plan zarzadu §1522-1526 wymaga */}
          {detail.userType === 'human' && !isDeleted && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                {t('tenant.users.drawer.sessions')}
              </h3>
              <p className="mb-2 text-xs text-text-tertiary">
                {t('tenant.users.drawer.sessionsDescription')}
              </p>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<LogOut size={14} aria-hidden="true" />}
                onClick={() => onLogoutAll(detail)}
                data-testid="user-drawer-logout-all"
              >
                {t('tenant.users.actions.logoutAll')}
              </Button>
            </section>
          )}

          {/* Sekcja: Akcje konta */}
          {detail.userType === 'human' && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                {t('tenant.users.drawer.accountActions')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {isDeleted ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<RotateCcw size={14} aria-hidden="true" />}
                    onClick={() => onRestore(detail)}
                    data-testid="user-drawer-restore"
                  >
                    {t('tenant.users.actions.restore')}
                  </Button>
                ) : (
                  <>
                    {detail.status === 'suspended' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<ShieldCheck size={14} aria-hidden="true" />}
                        onClick={() => onActivate(detail)}
                        data-testid="user-drawer-activate"
                      >
                        {t('tenant.users.actions.activate')}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<ShieldAlert size={14} aria-hidden="true" />}
                        onClick={() => onSuspend(detail)}
                        data-testid="user-drawer-suspend"
                      >
                        {t('tenant.users.actions.suspend')}
                      </Button>
                    )}
                    {detail.status === 'invited' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<Mail size={14} aria-hidden="true" />}
                        onClick={() => onResendInvite(detail)}
                        data-testid="user-drawer-resend"
                      >
                        {t('tenant.users.actions.resendInvite')}
                      </Button>
                    )}
                    {detail.status !== 'invited' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<KeyRound size={14} aria-hidden="true" />}
                        onClick={() => onTriggerReset(detail)}
                        data-testid="user-drawer-reset-password"
                      >
                        {t('tenant.users.actions.triggerPasswordReset')}
                      </Button>
                    )}
                    {/* ADR-0022: change-identifier (gated users.change_identifier) */}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onChangeIdentifier(detail)}
                      data-testid="user-drawer-change-identifier"
                    >
                      {t('tenant.users.actions.changeIdentifier')}
                    </Button>
                    {/* ADR-0022: set-temporary-password (gated users.set_temporary_password) */}
                    {detail.status !== 'invited' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onSetTempPassword(detail)}
                        data-testid="user-drawer-set-temp-password"
                      >
                        {t('tenant.users.actions.setTempPassword')}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Trash2 size={14} aria-hidden="true" />}
                      onClick={() => onDelete(detail)}
                      className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                      data-testid="user-drawer-delete"
                    >
                      {t('tenant.users.actions.delete')}
                    </Button>
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}

function DrawerBasicsSection({ detail }: { detail: UserDetail }) {
  const { t } = useTranslation();
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('tenant.users.drawer.basics')}
      </h3>
      <dl className="grid grid-cols-2 gap-y-2 text-sm">
        <dt className="text-text-muted">{t('tenant.users.drawer.email')}</dt>
        <dd className="text-text-primary">{detail.email}</dd>
        <dt className="text-text-muted">{t('tenant.users.drawer.displayName')}</dt>
        <dd className="text-text-primary">
          {detail.displayName ?? <span className="text-text-muted">—</span>}
        </dd>
        <dt className="text-text-muted">{t('tenant.users.drawer.type')}</dt>
        <dd>
          <UserTypeBadge userType={detail.userType} />
        </dd>
        <dt className="text-text-muted">{t('tenant.users.drawer.status')}</dt>
        <dd>
          <UserStatusBadge status={detail.status} />
        </dd>
        <dt className="text-text-muted">{t('tenant.users.drawer.role')}</dt>
        <dd>
          {detail.isSuperUser ? (
            <Badge color="amber">{t('tenant.users.badge.superUser')}</Badge>
          ) : (
            <Badge color="gray">{t('tenant.users.badge.regular')}</Badge>
          )}
        </dd>
        <dt className="text-text-muted">{t('tenant.users.drawer.totp')}</dt>
        <dd>
          {detail.totpEnabled ? (
            <Badge color="green">{t('tenant.users.drawer.totpOn')}</Badge>
          ) : (
            <Badge color="gray">{t('tenant.users.drawer.totpOff')}</Badge>
          )}
        </dd>
      </dl>
    </section>
  );
}

function DrawerCompaniesSection({ detail }: { detail: UserDetail }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const userPermsQ = useUserPermissions(detail.id);
  const rolesQ = useRoleTemplates();
  const companiesQ = useCompanies();

  const [showAddCompany, setShowAddCompany] = useState(false);

  const assignMutation = useMutation({
    mutationFn: async (input: {
      companyId: string;
      /** Komplet ról w kolejności (ADR-0103). Gdy podany — wygrywa z `roleTemplateId`. */
      roleTemplateIds?: string[];
      roleTemplateId?: string;
    }) => {
      const body: Record<string, unknown> = { userId: detail.id };
      if (input.roleTemplateIds) body.roleTemplateIds = input.roleTemplateIds;
      else body.roleTemplateId = input.roleTemplateId;
      const res = await api.post(`/companies/${input.companyId}/members`, body);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(detail.id) });
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(detail.id) });
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({ variant: 'success', title: t('tenant.users.toast.roleSaved') });
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.users.toast.roleSaveError'),
  });

  // Wsadowe przypisanie do wielu firm jedną rolą (multi-select) — jedno żądanie.
  const batchAssignMutation = useMutation({
    mutationFn: async (input: { companyIds: string[]; roleKeys: string[] }) => {
      const res = await api.post('/companies/members/batch', { userId: detail.id, ...input });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(detail.id) });
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(detail.id) });
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({ variant: 'success', title: t('tenant.users.toast.roleSaved') });
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.users.toast.roleSaveError'),
  });

  const revokeMutation = useMutation({
    mutationFn: async (companyId: string) => {
      const res = await api.delete(`/companies/${companyId}/members/${detail.id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(detail.id) });
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(detail.id) });
      queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
      toast.show({ variant: 'success', title: t('tenant.users.toast.companyRevoked') });
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (code === 'LAST_COMPANY_ACCESS') {
        toast.show({
          variant: 'warning',
          title: t('tenant.users.toast.lastCompanyTitle'),
          description: t('tenant.users.toast.lastCompanyDescription'),
          duration: 7000,
        });
        return;
      }
      handleApiError(err, t, toast, 'tenant.users.toast.companyRevokeError');
    },
  });

  if (detail.isSuperUser) {
    return (
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('tenant.users.drawer.companiesAccess')}
        </h3>
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
          data-testid="drawer-super-user-info"
        >
          {t('tenant.users.drawer.superUserBypass')}
        </div>
      </section>
    );
  }

  const accesses = userPermsQ.data?.companies ?? [];
  const allCompanies = companiesQ.data?.companies ?? [];
  const roles = rolesQ.data?.roleTemplates ?? [];

  const availableCompanies = allCompanies.filter(
    (c) => !accesses.some((a) => a.companyId === c.id),
  );

  const busy = assignMutation.isPending || revokeMutation.isPending;

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('tenant.users.drawer.companiesAccess')}
        </h3>
      </div>

      {userPermsQ.isLoading ? (
        <div className="flex items-center justify-center py-4" role="status">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-2 text-sm" data-testid="drawer-permissions-panel">
          {accesses.length > 0 && <InfoHint>{t('tenant.users.roles.hint')}</InfoHint>}
          {accesses.length === 0 && !showAddCompany && (
            <p className="text-sm text-text-muted">{t('tenant.users.drawer.noCompanies')}</p>
          )}
          {accesses.map((access: UserCompanyAccess) => (
            <CompanyAccessRow
              key={access.companyId}
              access={access}
              roles={roles}
              onRolesChange={(roleIds) =>
                assignMutation.mutate({ companyId: access.companyId, roleTemplateIds: roleIds })
              }
              onRevoke={() => revokeMutation.mutate(access.companyId)}
              busy={busy}
            />
          ))}

          {showAddCompany ? (
            <AddCompanyForm
              available={availableCompanies}
              roles={roles}
              onCancel={() => setShowAddCompany(false)}
              onSubmit={(companyIds, roleKeys) => {
                batchAssignMutation.mutate({ companyIds, roleKeys });
                setShowAddCompany(false);
              }}
            />
          ) : availableCompanies.length > 0 ? (
            <button
              type="button"
              onClick={() => setShowAddCompany(true)}
              className="flex items-center gap-2 rounded-lg border border-dashed border-border-secondary px-3 py-2 text-sm text-accent-700 hover:bg-accent-50 dark:hover:bg-accent-900/20"
              data-testid="drawer-add-company-btn"
            >
              <Plus size={14} aria-hidden="true" />
              {t('tenant.users.drawer.addCompany')}
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Modal: Dodaj usera (Czlowiek vs API/Serwis) + opcjonalne pola company + role
// ============================================================================

interface CreateUserForm {
  kind: 'human' | 'api' | 'service';
  email: string;
  username: string;
  displayName: string;
  companyId: string;
  roleTemplateId: string;
}

const CREATE_USER_EMPTY: CreateUserForm = {
  kind: 'human',
  email: '',
  username: '',
  displayName: '',
  companyId: '',
  roleTemplateId: '',
};

interface CreateUserModalProps {
  open: boolean;
  companies: CompanyListItem[];
  onClose: () => void;
  onCreated: () => void;
}

function CreateUserModal({ open, companies, onClose, onCreated }: CreateUserModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const rolesQ = useRoleTemplates();

  // Draft persistuje w sessionStorage — zamkniecie modala i ponowne otwarcie
  // przywraca wpisane dane. clearForm() na success: czysci draft.
  // ADR-0022: `username` w drafcie razem z reszta — tez chcemy zachowac przy
  // przypadkowym zamknieciu. `usernameError` zostaje transient (validation
  // result, nie user input — resetujemy przy zamknieciu).
  const [form, setForm, clearForm] = useFormDraft('user-create', CREATE_USER_EMPTY);
  const { kind, email, username, displayName, companyId, roleTemplateId } = form;
  const setKind = (v: 'human' | 'api' | 'service') => setForm((f) => ({ ...f, kind: v }));
  const setEmail = (v: string) => setForm((f) => ({ ...f, email: v }));
  const setUsername = (v: string) => setForm((f) => ({ ...f, username: v }));
  const setDisplayName = (v: string) => setForm((f) => ({ ...f, displayName: v }));
  const setCompanyId = (v: string) => setForm((f) => ({ ...f, companyId: v }));
  const setRoleTemplateId = (v: string) => setForm((f) => ({ ...f, roleTemplateId: v }));
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Resetuj transient state (validation + submitting) przy zamknieciu — form draft persistuje.
  useEffect(() => {
    if (!open) {
      setUsernameError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    // ADR-0022: human invite wymaga loginu.
    if (kind === 'human') {
      const u = username.trim();
      if (!u) {
        setUsernameError(t('tenant.users.createModal.errors.USERNAME_REQUIRED'));
        return;
      }
      if (!isValidUsername(u)) {
        setUsernameError(t('tenant.users.createModal.errors.USERNAME_FORMAT'));
        return;
      }
      setUsernameError(null);
    }
    setSubmitting(true);
    try {
      if (kind === 'human') {
        const body: Record<string, unknown> = { email, username: username.trim() };
        if (displayName) body.displayName = displayName;
        if (companyId) body.companyId = companyId;
        if (roleTemplateId) body.roleTemplateId = roleTemplateId;
        await api.post('/users/invite', body);
        toast.show({
          variant: 'success',
          title: t('tenant.users.createModal.toast.invited'),
        });
      } else {
        await api.post('/users', {
          email,
          displayName: displayName || undefined,
          userType: kind,
        });
        toast.show({
          variant: 'success',
          title: t('tenant.users.createModal.toast.created'),
          description: t('tenant.users.createModal.toast.tokenComingF11'),
          duration: 8000,
        });
      }
      clearForm();
      onCreated();
    } catch (err) {
      const code = extractErrorCode(err);
      // Serwer odrzucil walidacja pol (Zod: VALIDATION_ERROR z `details`). Pokaz blad
      // przy wlasciwym polu zamiast generycznego toastu (mirror walidacji klienckiej).
      if (code === 'VALIDATION_ERROR') {
        const fields = extractFieldErrors(err);
        if (kind === 'human' && fields.username?.length) {
          setUsernameError(t('tenant.users.createModal.errors.USERNAME_FORMAT'));
          return;
        }
        toast.show({
          variant: 'error',
          title: t('tenant.users.createModal.toast.errorTitle'),
          description: t('tenant.users.createModal.errors.VALIDATION'),
        });
        return;
      }
      const knownCodes = [
        'EMAIL_ALREADY_EXISTS_IN_TENANT',
        'EMAIL_ALREADY_EXISTS_IN_ADM',
        'USERNAME_ALREADY_EXISTS_IN_TENANT',
        'COMPANY_CONTEXT_REQUIRED',
        'COMPANY_NOT_FOUND',
        'COMPANY_REQUIRED_FOR_ROLE',
        'ROLE_TEMPLATE_NOT_FOUND',
        'ROLE_NOT_IN_COMPANY',
        'PERMISSION_DENIED',
      ];
      const codeKey = knownCodes.includes(code)
        ? `tenant.users.createModal.errors.${code}`
        : 'tenant.users.createModal.errors.generic';
      toast.show({
        variant: 'error',
        title: t('tenant.users.createModal.toast.errorTitle'),
        description: t(codeKey),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled = !email || (roleTemplateId !== '' && companyId === '');

  return (
    <Modal
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      title={t('tenant.users.createModal.title')}
      description={t('tenant.users.createModal.subtitle')}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitDisabled}
            data-testid="create-user-submit"
          >
            {kind === 'human'
              ? t('tenant.users.createModal.submitInvite')
              : t('tenant.users.createModal.submitCreate')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-text-secondary">
            {t('tenant.users.createModal.kindLegend')}
          </legend>
          <div className="space-y-2">
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="user-kind"
                value="human"
                checked={kind === 'human'}
                onChange={() => setKind('human')}
                data-testid="create-user-kind-human"
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium text-text-primary">
                  {t('tenant.users.createModal.kindHuman')}
                </span>
                <span className="ml-1 block text-xs text-text-tertiary">
                  {t('tenant.users.createModal.kindHumanHint')}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="user-kind"
                value="api"
                checked={kind === 'api'}
                onChange={() => setKind('api')}
                data-testid="create-user-kind-api"
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium text-text-primary">
                  {t('tenant.users.createModal.kindApi')}
                </span>
                <span className="ml-1 block text-xs text-text-tertiary">
                  {t('tenant.users.createModal.kindApiHint')}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="user-kind"
                value="service"
                checked={kind === 'service'}
                onChange={() => setKind('service')}
                data-testid="create-user-kind-service"
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium text-text-primary">
                  {t('tenant.users.createModal.kindService')}
                </span>
                <span className="ml-1 block text-xs text-text-tertiary">
                  {t('tenant.users.createModal.kindServiceHint')}
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        {kind === 'human' && (
          <div className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
            {t('tenant.users.createModal.humanInfo')}{' '}
            <Link
              to="/employees"
              className="font-medium underline"
              data-testid="create-user-employees-link"
            >
              {t('tenant.users.createModal.humanInfoLink')}
            </Link>
          </div>
        )}

        <Input
          label={t('tenant.users.createModal.emailLabel')}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          data-testid="create-user-email"
        />
        {kind === 'human' && (
          <Input
            label={t('tenant.users.createModal.usernameLabel')}
            type="text"
            required
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (usernameError) setUsernameError(null);
            }}
            disabled={submitting}
            error={usernameError ?? undefined}
            hint={t('tenant.users.createModal.usernameHint')}
            data-testid="create-user-username"
          />
        )}
        <Input
          label={t('tenant.users.createModal.displayNameLabel')}
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={submitting}
          maxLength={150}
          data-testid="create-user-display-name"
        />

        {kind === 'human' && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label={t('tenant.users.createModal.companyLabel')}>
              <Dropdown
                value={companyId}
                onChange={(v) => setCompanyId(v)}
                disabled={submitting}
                data-testid="create-user-company"
                options={[
                  { value: '', label: t('tenant.users.createModal.companyPlaceholder') },
                  ...companies.map((c) => ({ value: c.id, label: c.shortName ?? c.legalName })),
                ]}
              />
            </FormField>
            <FormField
              label={t('tenant.users.createModal.roleLabel')}
              hint={
                roleTemplateId !== '' && !companyId
                  ? t('tenant.users.createModal.roleNeedsCompany')
                  : undefined
              }
            >
              <Dropdown
                value={roleTemplateId}
                onChange={(v) => setRoleTemplateId(v)}
                disabled={submitting || rolesQ.isLoading || !companyId}
                data-testid="create-user-role"
                options={[
                  { value: '', label: t('tenant.users.createModal.rolePlaceholder') },
                  ...(rolesQ.data?.roleTemplates ?? []).map((r) => ({
                    value: r.id,
                    label: r.name,
                  })),
                ]}
              />
            </FormField>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ============================================================================
// Modal: Zawies (wymaga reason)
// ============================================================================

function SuspendUserModal({
  user,
  onClose,
  onConfirm,
  submitting,
}: {
  user: UserListItem | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!user) setReason('');
  }, [user]);

  if (!user) return null;

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t('tenant.users.suspendModal.title')}
      description={t('tenant.users.suspendModal.subtitle', {
        name: user.displayName ?? user.email,
      })}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => onConfirm(reason.trim())}
            loading={submitting}
            disabled={reason.trim().length === 0}
            data-testid="suspend-user-confirm"
          >
            {t('tenant.users.suspendModal.confirm')}
          </Button>
        </>
      }
    >
      <Input
        label={t('tenant.users.suspendModal.reasonLabel')}
        type="text"
        required
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={submitting}
        maxLength={500}
        data-testid="suspend-user-reason"
        hint={t('tenant.users.suspendModal.reasonHint')}
      />
    </Modal>
  );
}

// ============================================================================
// Modal: Usun (confirm)
// ============================================================================

function DeleteUserModal({
  user,
  onClose,
  onConfirm,
  submitting,
}: {
  user: UserListItem | null;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  if (!user) return null;
  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t('tenant.users.deleteModal.title')}
      description={t('tenant.users.deleteModal.subtitle', {
        name: user.displayName ?? user.email,
      })}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            loading={submitting}
            data-testid="delete-user-confirm"
          >
            {t('tenant.users.deleteModal.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-tertiary">{t('tenant.users.deleteModal.body')}</p>
    </Modal>
  );
}

// ============================================================================
// Modal: Wyloguj ze wszystkich sesji (confirm)
// ============================================================================

function LogoutAllUserModal({
  user,
  onClose,
  onConfirm,
  submitting,
}: {
  user: UserListItem | null;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  if (!user) return null;
  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t('tenant.users.logoutAllModal.title')}
      description={t('tenant.users.logoutAllModal.subtitle', {
        name: user.displayName ?? user.email,
      })}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            loading={submitting}
            data-testid="logout-all-confirm"
          >
            {t('tenant.users.logoutAllModal.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-tertiary">{t('tenant.users.logoutAllModal.body')}</p>
    </Modal>
  );
}

// ============================================================================
// Modal: Wyslij link resetu hasla (confirm)
// ============================================================================

function TriggerPasswordResetModal({
  user,
  onClose,
  onConfirm,
  submitting,
}: {
  user: UserListItem | null;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  if (!user) return null;
  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t('tenant.users.triggerResetModal.title')}
      description={t('tenant.users.triggerResetModal.subtitle', {
        name: user.displayName ?? user.email,
        email: user.email,
      })}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onConfirm} loading={submitting} data-testid="trigger-reset-confirm">
            {t('tenant.users.triggerResetModal.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-tertiary">{t('tenant.users.triggerResetModal.body')}</p>
    </Modal>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function extractErrorCode(err: unknown): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    if (typeof data?.error === 'string') return data.error;
    if (typeof data?.code === 'string') return data.code;
  }
  return 'UNKNOWN';
}

function handleApiError(
  err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
  toast: ReturnType<typeof useToast>,
  fallbackTitleKey: string,
) {
  const code = extractErrorCode(err);
  const knownCodes = [
    'ALREADY_SUSPENDED',
    'NOT_SUSPENDED',
    'NOT_INVITED',
    'NOT_DELETED',
    'CANNOT_SUSPEND_SELF',
    'CANNOT_DELETE_SELF',
    'CANNOT_LOGOUT_SELF',
    'ALREADY_DELETED',
    'USER_DELETED',
    'USER_INVITED',
    'INVALID_USER_TYPE',
    'EMAIL_CONFLICT_AFTER_DELETE',
    'LAST_COMPANY_ACCESS',
    'SYSTEM_ROLE_IMMUTABLE',
    'PERMISSION_DENIED',
  ];
  const description = knownCodes.includes(code)
    ? t(`tenant.users.errors.${code}`)
    : t('tenant.users.errors.generic');
  toast.show({
    variant: 'error',
    title: t(fallbackTitleKey),
    description,
  });
}

/**
 * Data i godzina ostatniego logowania (bezwzględna, np. „16.09.2026, 12:34").
 * Eugenia 2026-09-16: kolumna ma pokazywać konkretny moment, nie „X minut temu".
 * `toLocaleString` z krótkim stylem daty+czasu, locale wg języka UI (mirror wzorca
 * dla pola typu `timestamp` w `lib/ext-list-columns.tsx`).
 */
function formatLastLogin(iso: string | null, language: string, t: (key: string) => string): string {
  if (!iso) return t('tenant.users.lastLoginNever');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return t('tenant.users.lastLoginNever');
  const locale = language?.startsWith('en') ? 'en-GB' : 'pl-PL';
  return d.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Konwersja UserDetail (z drawera) na UserListItem (kontrakt modala suspend/delete).
 * Wszystkie wymagane pola z tabeli sa w detail.
 */
function userDetailToListItem(u: UserDetail): UserListItem {
  return {
    id: u.id,
    email: u.email,
    displayName: u.displayName,
    userType: u.userType,
    status: u.status,
    isSuperUser: u.isSuperUser,
    defaultCompanyId: u.defaultCompanyId,
    hasAccount: u.hasAccount,
    companies: u.companies.map((c) => ({
      id: c.id,
      legalName: c.legalName,
      shortName: c.shortName,
      isManager: c.isManager,
      roles: c.roles,
      roleTemplateId: c.roleTemplateId,
      roleTemplateName: c.roleTemplateName ?? null,
    })),
    lastLoginAt: u.lastLoginAt,
    createdAt: u.createdAt,
    gcrecord: u.gcrecord,
  };
}
